import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { SiteWordmark } from '@/components/branding/SiteWordmark'
import { ForumSeo } from '@/components/ForumSeo'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import {
  forumAlertError,
  forumCard,
  forumCardHeader,
  forumField,
  forumKicker,
  forumLink,
  forumMutedSm,
  forumTitleLg,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

export function SignUpPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') ?? '/'
  const { reload } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="nk-container flex flex-1 flex-col py-10 md:items-center md:justify-center md:py-14">
      <ForumSeo
        title={forumPageTitle('Register')}
        description={truncateForMetaDescription(
          'Create an NK Our Town neighbor account for the North Kingstown RI community forum.',
        )}
        noindex
      />
      <div className="mx-auto mb-6 flex w-full max-w-xl justify-center md:mb-8">
        <Link to="/" className="rounded-sm no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-100">
          <SiteWordmark className="h-9 sm:h-10" />
          <span className="sr-only">NK Our Town — home</span>
        </Link>
      </div>
      <main className={`mx-auto w-full max-w-xl ${forumCard}`}>
        <header className={forumCardHeader}>
          <p className={forumKicker}>Account</p>
          <h1 className={forumTitleLg}>Register</h1>
          <p className={`${forumMutedSm} mt-2 text-zinc-600`}>
            Username appears in @mentions and on your profile. Password at least 12 characters.
          </p>
        </header>

        <div className="space-y-4 px-5 py-5 md:px-6 md:py-6">
          {error ? <div role="alert" className={forumAlertError}>{error}</div> : null}

          <form
            className="space-y-4"
            aria-label="Register new account"
            onSubmit={async evt => {
              evt.preventDefault()
              try {
                setError(null)
                await apiFetch(`/api/auth/sign-up`, {
                  method: 'POST',
                  json: { email, password, username, displayName },
                })
                await reload()
                navigate(next)
              } catch (err: unknown) {
                setError(
                  typeof err === 'object' && err && 'message' in err
                    ? String((err as Error).message)
                    : 'Signup blocked.',
                )
              }
            }}
          >
            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signup-display">
                Display name
              </label>
              <input id="signup-display" value={displayName} onChange={evt => setDisplayName(evt.target.value)} className={forumField} required />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signup-user">
                Username
              </label>
              <input
                id="signup-user"
                value={username}
                onChange={evt => setUsername(evt.target.value)}
                className={forumField}
                autoComplete="username"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signup-email">
                Email
              </label>
              <input
                id="signup-email"
                value={email}
                onChange={evt => setEmail(evt.target.value)}
                className={forumField}
                type="email"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-semibold text-zinc-800" htmlFor="signup-password">
                Password (min 12 characters)
              </label>
              <input
                id="signup-password"
                value={password}
                onChange={evt => setPassword(evt.target.value)}
                className={forumField}
                type="password"
                autoComplete="new-password"
                minLength={12}
                required
              />
            </div>

            <div className="pt-2">
              <Button type="submit" className="w-full md:w-auto">
                Create account
              </Button>
            </div>
          </form>

          <p className="border-t border-zinc-100 pt-4 text-[14px] text-zinc-600">
            Already registered?{' '}
            <Link className={forumLink} to={`/sign-in?next=${encodeURIComponent(next)}`}>
              Sign in
            </Link>
          </p>
        </div>
      </main>
    </div>
  )
}
